import React, { useCallback, useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import api from '../utils/axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Table, TableBody, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { toast } from 'sonner';
import { FileSearch, RefreshCw, Search } from 'lucide-react';
import { CuentaSinIdentificarFila, MOTIVOS } from '../components/cuentas/CuentaSinIdentificarFila';
import { AsignarCuentaDialog, DescartarCuentaDialog } from '../components/cuentas/CuentaSinIdentificarDialogs';

const COLUMNAS = ['CVU', 'Sponsor', 'Motivo', 'CUIT detectado', 'Primer movimiento', 'Último movimiento', 'Cantidad', 'Volumen', 'En espera', ''];

const Resumen = ({ conteo, motivo, onMotivo }) => (
  <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 mb-6">
    {Object.entries(MOTIVOS).map(([k, m]) => (
      <button key={k} onClick={() => onMotivo(motivo === k ? 'todos' : k)} data-testid={`csi-resumen-${k}`}
        className={`text-left p-4 rounded-xl border bg-white transition-[box-shadow,border-color] hover:shadow-md ${
          motivo === k ? 'border-blue-400 ring-2 ring-blue-100' : 'border-slate-200'}`}>
        <p className="text-3xl font-bold text-slate-900 tabular-nums">{conteo?.PENDIENTE?.[k] || 0}</p>
        <p className="text-sm text-slate-500">{m.label} · pendientes</p>
      </button>
    ))}
  </div>
);

export default function CuentasSinIdentificar() {
  const [params] = useSearchParams();
  const [filtros, setFiltros] = useState({ estado: 'PENDIENTE', motivo: params.get('motivo') || 'todos', busqueda: params.get('cvu') || '' });
  const [data, setData] = useState({ items: [], total: 0, conteo: {}, puede_actuar: false });
  const [cargando, setCargando] = useState(true);
  const [asignar, setAsignar] = useState(null);
  const [descartar, setDescartar] = useState(null);

  const cargar = useCallback(async () => {
    setCargando(true);
    try {
      const q = { estado: filtros.estado, limit: 500 };
      if (filtros.motivo !== 'todos') q.motivo = filtros.motivo;
      if (filtros.busqueda.trim()) q.busqueda = filtros.busqueda.trim();
      const res = await api.get('/cuentas-sin-identificar', { params: q });
      setData(res.data);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'No se pudieron cargar las cuentas');
    } finally {
      setCargando(false);
    }
  }, [filtros]);

  useEffect(() => { cargar(); }, [cargar]);
  const set = (k) => (v) => setFiltros((f) => ({ ...f, [k]: v }));
  const listo = () => { setAsignar(null); setDescartar(null); cargar(); };

  return (
    <div className="p-6 lg:p-8 h-full overflow-auto bg-slate-50" data-testid="cuentas-sin-identificar-page">
      <div className="flex items-start gap-3 mb-6">
        <div className="p-2.5 rounded-xl bg-blue-100"><FileSearch size={22} className="text-blue-700" /></div>
        <div>
          <h1 className="text-3xl font-heading font-bold tracking-tight text-slate-900">Cuentas sin identificar</h1>
          <p className="text-slate-600 mt-1 max-w-3xl text-sm">
            CVU de Waya que llegaron sin CUIT, con un CUIT inválido o con un CUIT distinto del que tienen en el padrón.
            Mientras están pendientes no generan casos: sus alertas quedan en espera hasta que se asignan a un cliente.
          </p>
        </div>
      </div>

      <Resumen conteo={data.conteo} motivo={filtros.motivo} onMotivo={set('motivo')} />

      <div className="flex flex-wrap gap-3 mb-4">
        <Select value={filtros.estado} onValueChange={set('estado')}>
          <SelectTrigger className="w-44 bg-white" data-testid="csi-filtro-estado"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="PENDIENTE">Pendientes</SelectItem>
            <SelectItem value="ASIGNADA">Asignadas</SelectItem>
            <SelectItem value="DESCARTADA">Descartadas</SelectItem>
            <SelectItem value="TODAS">Todas</SelectItem>
          </SelectContent>
        </Select>
        <Select value={filtros.motivo} onValueChange={set('motivo')}>
          <SelectTrigger className="w-56 bg-white" data-testid="csi-filtro-motivo"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value="todos">Todos los motivos</SelectItem>
            {Object.entries(MOTIVOS).map(([k, m]) => <SelectItem key={k} value={k}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
        <div className="relative flex-1 min-w-[220px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
          <Input value={filtros.busqueda} onChange={(e) => set('busqueda')(e.target.value)} placeholder="Buscar por CVU, CUIT o nombre…"
            className="pl-9 bg-white" data-testid="csi-busqueda" />
        </div>
        <Button variant="outline" onClick={cargar} className="bg-white" data-testid="csi-refrescar">
          <RefreshCw size={16} className={cargando ? 'animate-spin' : ''} />
        </Button>
      </div>

      <Card className="bg-white border-slate-200 overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>{COLUMNAS.map((c, i) => (
              <TableHead key={c || i} className={`text-xs uppercase text-slate-500 ${['Cantidad', 'Volumen'].includes(c) ? 'text-right' : ''}`}>{c}</TableHead>
            ))}</TableRow>
          </TableHeader>
          <TableBody>
            {data.items.map((c) => (
              <CuentaSinIdentificarFila key={c.cvu} c={c} puedeActuar={data.puede_actuar} onAsignar={setAsignar} onDescartar={setDescartar} />
            ))}
          </TableBody>
        </Table>
        {!cargando && data.items.length === 0 && (
          <p className="text-center text-slate-500 py-10 text-sm" data-testid="csi-vacio">No hay cuentas para estos filtros</p>
        )}
        <p className="px-4 py-2 text-xs text-slate-400 border-t border-slate-100" data-testid="csi-total">{data.total} cuenta(s)</p>
      </Card>

      {asignar && <AsignarCuentaDialog cuenta={asignar} onClose={() => setAsignar(null)} onDone={listo} />}
      {descartar && <DescartarCuentaDialog cuenta={descartar} onClose={() => setDescartar(null)} onDone={listo} />}
    </div>
  );
}
